import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import LibraryPanel from "@/components/LibraryPanel";
import { searchLibrary } from "@/lib/atlasApi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { BookOpen, Search, Loader2, FileText, X, BookMarked } from "lucide-react";

function Highlight({ text }) {
  const parts = (text || "").split("**");
  return (
    <span>
      {parts.map((p, i) =>
        i % 2 === 1 ? (
          <mark key={i} className="bg-[#E05A2B]/30 text-[#F6A06B] rounded px-0.5">{p}</mark>
        ) : (
          <span key={i}>{p}</span>
        )
      )}
    </span>
  );
}

export default function LibraryPage() {
  const { isAdmin } = useOutletContext();
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);
  const [viewer, setViewer] = useState(null);

  const run = async () => {
    const term = q.trim();
    if (term.length < 2 || busy) return;
    setBusy(true);
    try { const d = await searchLibrary(term, 12); setResults(d.results || []); }
    catch { setResults([]); }
    finally { setBusy(false); }
  };

  return (
    <div className="h-full flex flex-col bg-[#0B0F17]" data-testid="library-page">
      <div className="px-6 h-14 flex items-center gap-2 border-b border-[#2A364F] shrink-0">
        <BookOpen className="h-5 w-5 text-[#E05A2B]" />
        <div>
          <h1 className="text-base font-semibold text-gray-100 leading-tight">Biblioteca Científica</h1>
          <p className="text-[11px] text-slate-500" style={{ fontFamily: "JetBrains Mono, monospace" }}>consulta + material de estudos mestre do SousChef</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Busca avançada */}
        <div className="max-w-4xl w-full mx-auto p-6">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
              <Input
                data-testid="library-search-input"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && run()}
                placeholder='Busque por ingrediente, receita ou termo (ex: "polvo", "defumação", "umami")'
                className="pl-9 h-11 bg-[#111827] border-[#2A364F]"
              />
            </div>
            <Button data-testid="library-search-btn" onClick={run} disabled={busy} className="bg-[#E05A2B] hover:bg-[#F06B3C] text-white h-11 px-5">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Buscar"}
            </Button>
          </div>

          {results !== null && (
            <div className="mt-5 space-y-3" data-testid="library-results">
              {results.length === 0 && <p className="text-sm text-slate-500">Nenhuma ocorrência encontrada no acervo.</p>}
              {results.map((r, i) => (
                <button
                  key={i}
                  data-testid={`library-result-${i}`}
                  onClick={() => setViewer(r)}
                  className="w-full text-left flex gap-3 rounded-xl border border-[#2A364F] bg-[#161E2E] p-3 hover:border-[#E05A2B]/60 transition-colors"
                >
                  {/* capa/miniatura estilo livro */}
                  <div className="shrink-0 w-12 h-16 rounded-md bg-gradient-to-b from-[#1b2740] to-[#0B0F17] border border-[#2A364F] flex items-center justify-center">
                    <BookMarked className="h-5 w-5 text-[#E05A2B]" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold text-gray-100 truncate">{r.title || r.source}</span>
                      <Badge variant="outline" className="border-[#2A364F] text-slate-400 shrink-0">
                        {Math.round((r.score || 0) * 100)}%{r.keyword_match ? " · termo" : ""}
                      </Badge>
                    </div>
                    <p className="text-xs text-slate-400 mt-1 line-clamp-2"><Highlight text={r.snippet} /></p>
                    <div className="text-[10px] text-slate-500 mt-1 font-mono">
                      bloco #{r.chunk_index}{r.page ? ` · pág. ${r.page}` : ""}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Acervo (upload + registro) */}
        <div className="max-w-3xl w-full mx-auto border-t border-[#2A364F]">
          <LibraryPanel isAdmin={isAdmin} />
        </div>
      </div>

      {/* Visor interno */}
      {viewer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" data-testid="library-viewer" onClick={() => setViewer(null)}>
          <div className="w-full max-w-2xl max-h-[80vh] rounded-2xl border border-[#2A364F] bg-[#111827] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 h-12 border-b border-[#2A364F]">
              <span className="inline-flex items-center gap-2 text-sm font-medium text-gray-100">
                <FileText className="h-4 w-4 text-[#E05A2B]" /> {viewer.title || viewer.source}
              </span>
              <button onClick={() => setViewer(null)} className="text-slate-500 hover:text-slate-200" data-testid="library-viewer-close"><X className="h-4 w-4" /></button>
            </div>
            <div className="p-5 overflow-y-auto">
              <div className="text-[11px] text-slate-500 mb-2 font-mono">
                bloco #{viewer.chunk_index}{viewer.page ? ` · página ${viewer.page}` : " · (página exata disponível após ingestão com Object Storage)"}
              </div>
              <p className="text-sm text-slate-200 leading-relaxed whitespace-pre-wrap"><Highlight text={(viewer.text || "").replace(new RegExp(`(${(q || "").replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`, "ig"), "**$1**")} /></p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
